/**
 * Load-test lab (spec 2026-09-26 rev 2).
 *
 * Every label here states what was measured and nothing more: a staging run says nothing about
 * production, a generator-limited run is not an application result, and a stop the executor
 * never confirmed is shown as unconfirmed, in red, not as stopped.
 */

import { useState } from 'react';
import { Reading } from '../Reading';
import {
  approvePlan, cancelRun, getTestPlans, getTestRuns, startRun,
  type ApiResult, type TestPlanView, type TestRunView,
} from '../api';
import { usePoll } from '../usePoll';
import { Stale, Unavailable } from './Console';
import { bytes, mediaPlanLines, num, pct, secs, stalledShare } from '../video-format';
import { central, centralClock, centralWindow } from '../time';

const ACTIVE = ['queued', 'claimed', 'running', 'stopping'];
const gb = (b: number) => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} GB` : `${(b / 1e6).toFixed(1)} MB`);

function failureText(r: ApiResult<unknown>): string | null {
  if (r.ok) return null;
  const f = r.failure;
  if (f.kind === 'conflict') return 'The plan or its binding changed since this page loaded. Nothing was done; the list will refresh.';
  if (f.kind === 'unauthenticated') return 'Your session has ended. Sign in again.';
  return f.reason;
}

export function LoadTests() {
  const plans = usePoll(getTestPlans, 20_000);
  const runs = usePoll(getTestRuns, 5_000);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<ApiResult<unknown>>, done: string) {
    setBusy(true);
    const r = await fn();
    setBusy(false);
    setMsg(failureText(r) ?? done);
  }

  if (plans.loading && !plans.data) return <Reading label="Load-test plans" state="unknown" source="loading" reason="Reading." />;
  if (!plans.data) return <Unavailable what="Load-test plans" failure={plans.failure!} />;
  const envOf = new Map(plans.data.plans.map((p) => [p.id, p.plan.target.env]));

  return (
    <>
      {plans.failure && <Stale failure={plans.failure} />}
      {msg && <div className="notice blue"><p>{msg}</p></div>}

      <h3>Plans</h3>
      {plans.data.plans.length === 0 && <p className="alert-summary">No plan is loaded from the load-tests repository.</p>}
      <div className="alert-list">
        {plans.data.plans.map((p) => (
          <PlanRow key={p.id} p={p} canApprove={plans.data!.canApprove} busy={busy}
            onApprove={() => act(() => approvePlan(p.id, p.current), `Approved ${p.id}.`)}
            onRun={() => act(() => startRun(p.id), `Run requested for ${p.id}.`)} />
        ))}
      </div>
      {plans.data.rejected.length > 0 && (
        <div className="notice red">
          <h3>Plans refused by validation</h3>
          {plans.data.rejected.map((r) => <p key={r.file}><code>{r.file}</code>: {r.errors.join('; ')}</p>)}
        </div>
      )}

      <h3>Runs</h3>
      {runs.failure && (runs.data ? <Stale failure={runs.failure} /> : <Unavailable what="Load-test runs" failure={runs.failure} />)}
      {runs.data && runs.data.runs.length === 0 && <p className="alert-summary">No run has been requested.</p>}
      <div className="alert-list">
        {runs.data?.runs.map((r) => (
          <RunRow key={r.id} r={r} env={envOf.get(r.planId) ?? null} busy={busy}
            onCancel={() => act(() => cancelRun(r.id), `Cancel requested for run #${r.id}.`)} />
        ))}
      </div>

      <div className="notice blue">
        <h3>A virtual user is not a viewer</h3>
        <p>
          A load generator's concurrency setting and the number of people watching video are different quantities.
          Results here state which one they measured, and a staging run says nothing about production capacity.
        </p>
      </div>
    </>
  );
}

function PlanRow({ p, canApprove, busy, onApprove, onRun }: { p: TestPlanView; canApprove: boolean; busy: boolean;
  onApprove: () => void; onRun: () => void }) {
  const t = p.plan.target;
  return (
    <div className={`alert-row ${p.approvalValid ? '' : 'sev-warning'}`}>
      <div className="alert-head">
        <span className={`pill ${p.approvalValid ? 'ok' : 'sev-warning'}`}>{p.approvalValid ? 'approved' : 'not approved'}</span>
        <strong>{p.id}</strong>
        <span className="pill muted">{p.plan.suite}</span>
        <span className="pill muted">{t.env}</span>
      </div>
      <p className="alert-summary">{p.plan.purpose}</p>
      <div className="alert-meta">
        <span>Target {t.hosts.join(', ')}:{t.port} {t.paths.join(' ')}</span>
        {p.plan.suite === 'media'
          ? mediaPlanLines(p.plan).map((l) => <span key={l}>{l}</span>)
          : <span>Stages {p.plan.workload.stages.map((s) => `${s.duration} → ${s.rps} rps`).join(', ')}; max {p.plan.workload.max_vus} VUs</span>}
        <span>Hard stop {p.plan.duration_max}; window {centralWindow(p.plan.window_utc)}; budget {gb(p.plan.budget_bytes)}</span>
        {p.plan.suite !== 'media' && (
          <span>Thresholds p95 {p.plan.thresholds.p95_ms} ms, errors {p.plan.thresholds.error_rate}; abort at p95 ×{p.plan.abort.p95_vs_baseline},
            5xx {p.plan.abort.error_rate}, PeerTube CPU {p.plan.abort.peertube_cpu}</span>
        )}
        <span>Commit {p.current.commit.slice(0, 10)}; image {p.current.imageDigest.slice(0, 19)}; ceilings {p.current.ceilingsVersion}</span>
        <span>Estimate {p.estimate.requests.toLocaleString()} requests, {gb(p.estimate.bytes)}. {p.estimate.assumption}</span>
        <span>At most {gb(p.maxTrafficAfterStop)} transferred after a stop decision.</span>
        <span>{p.approval ? `Approved by user ${p.approval.by} at ${central(p.approval.at)}` : 'Not approved'}</span>
      </div>
      {p.approval && !p.approvalValid && (
        <p className="alert-summary">Approval no longer matches: {p.mismatch.join(', ')}</p>
      )}
      {canApprove && (
        <p style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          {!p.approvalValid && <button className="action" disabled={busy} onClick={onApprove}>Approve</button>}
          {p.approvalValid && <button className="action" disabled={busy} onClick={onRun}>Run</button>}
        </p>
      )}
    </div>
  );
}

function RunRow({ r, env, busy, onCancel }: { r: TestRunView; env: string | null; busy: boolean;
  onCancel: () => void }) {
  const unconfirmed = r.status === 'stop_unconfirmed';
  return (
    <div className={`alert-row ${unconfirmed ? 'sev-critical' : ''}`}>
      <div className="alert-head">
        <span className={`pill ${unconfirmed ? 'sev-critical' : r.status === 'passed' ? 'ok' : 'muted'}`}>
          {unconfirmed ? 'Stop unconfirmed' : r.status}
        </span>
        <strong>#{r.id} {r.planId}</strong>
        {r.cancelRequested && !r.stopConfirmedAt && <span className="pill sev-warning">Cancel requested</span>}
        {r.generatorLimited && <span className="pill sev-warning">Generator-limited: not an application result</span>}
        {env === 'staging' && <span className="pill muted">Staging, not production</span>}
        {r.verified && <span className="pill ok">Verified</span>}
      </div>
      <div className="alert-meta">
        <span>Start {r.startedAt ? central(r.startedAt) : '—'}</span>
        <span>End {r.endedAt ? central(r.endedAt) : '—'}</span>
        {r.shardsTotal > 1 && <span>Shards {r.shardsClaimed}/{r.shardsTotal}</span>}
        {r.abortReason && <span>Reason: {r.abortReason}</span>}
      </div>
      {r.video && (
        <div className="alert-meta">
          <span>Viewers {num(r.video.viewers)}</span>
          <span>Start p95 {secs(r.video.startP95S)}</span>
          <span>Segment p95 {secs(r.video.segmentP95S)}</span>
          <span>Stalled {stalledShare(r.video)} of segments</span>
          <span>Segment errors {pct(r.video.segmentFailed)}</span>
          <span>Media {bytes(r.video.mediaBytes)}</span>
        </div>
      )}
      {/* Any signed-in operator may stop load; only approvers may start it. */}
      {ACTIVE.includes(r.status) && (
        <p style={{ marginTop: 10 }}><button className="ghost" disabled={busy} onClick={onCancel}>Cancel</button></p>
      )}
    </div>
  );
}

/** Traffic view annotation. States that the window includes synthetic traffic; claims no exclusion. */
export function TestTrafficNotice() {
  const runs = usePoll(getTestRuns, 30_000);
  if (!runs.data) return null;
  const now = Date.now(); const hourAgo = now - 3_600_000;
  const overlapping = runs.data.runs.filter((r) => r.startedAt && Date.parse(r.startedAt) <= now
    && (r.endedAt ? Date.parse(r.endedAt) : now) >= hourAgo);
  if (overlapping.length === 0) return null;
  return (
    <div className="notice red">
      <h3>Includes test traffic</h3>
      {overlapping.map((r) => (
        <p key={r.id}>
          Load-test run #{r.id} ({r.planId}) from {centralClock(r.startedAt!)} to {centralClock(r.endedAt ?? new Date(now))}.
          Request counts in this window include synthetic traffic.
        </p>
      ))}
    </div>
  );
}
